import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { RevealWords, FadeUp } from "@/components/site/Reveal";

const CHAPTERS = [
    {
        n: "01",
        title: "The Problem",
        body: "Most homes are approved on paper — 2D lines that cannot show light, texture, or how a room truly breathes. Costly regrets are born here.",
    },
    {
        n: "02",
        title: "Our Answer",
        body: "We translate your intent into immersive 3D — every material, shadow and sightline rehearsed until it feels inevitable.",
    },
    {
        n: "03",
        title: "The Promise",
        body: "You walk your home before it exists. You approve with your senses, not your imagination. You build once. You build right.",
    },
];

export default function About() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
    const bigY = useTransform(scrollYProgress, [0, 1], ["25%", "-25%"]);

    return (
        <section
            id="about"
            data-testid="about"
            ref={ref}
            className="relative py-28 md:py-40 overflow-hidden"
        >
            {/* Oversized watermark */}
            <motion.div
                aria-hidden
                style={{ y: bigY }}
                className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 text-center"
            >
                <span className="font-display text-[28vw] leading-none tracking-tighter text-foreground/[0.03] select-none whitespace-nowrap">
                    Manifesto
                </span>
            </motion.div>

            <div className="relative max-w-7xl mx-auto px-5 md:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-20">
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="divider-gold" />
                            <span className="uppercase tracking-[0.32em] text-[11px] text-gold font-medium">
                                Manifesto
                            </span>
                        </div>
                        <FadeUp>
                            <h2 className="font-display text-5xl md:text-6xl leading-[1] tracking-tight">
                                Why <span className="text-gold">CHANDAK</span><br />
                                BuildVision.
                            </h2>
                        </FadeUp>
                    </div>

                    <div className="lg:col-span-8 lg:pt-6">
                        <RevealWords
                            whileInView
                            text="A home is the largest ambition most families ever attempt. We believe you deserve to feel it — long before you fund it."
                            className="font-display text-2xl md:text-4xl leading-tight tracking-tight text-foreground/90"
                            stagger={0.03}
                        />
                    </div>
                </div>

                {/* Numbered chapters */}
                <div className="space-y-14 md:space-y-20">
                    {CHAPTERS.map((c, i) => (
                        <motion.article
                            key={c.n}
                            data-testid={`chapter-${c.n}`}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-100px" }}
                            transition={{ duration: 0.9, ease: [0.19, 1, 0.22, 1], delay: i * 0.05 }}
                            className="relative grid grid-cols-12 gap-6 md:gap-12 items-start border-t hairline pt-10 md:pt-14"
                        >
                            <div className="col-span-3 md:col-span-2">
                                <div className="font-display text-4xl md:text-6xl text-gold leading-none">
                                    {c.n}
                                </div>
                                <div className="mt-2 text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
                                    Chapter
                                </div>
                            </div>
                            <div className="col-span-9 md:col-span-6">
                                <h3 className="font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
                                    {c.title}
                                </h3>
                            </div>
                            <div className="col-span-12 md:col-span-4">
                                <p className="text-base md:text-lg text-foreground/70 leading-relaxed">
                                    {c.body}
                                </p>
                            </div>
                        </motion.article>
                    ))}
                </div>

                {/* Signature line */}
                <FadeUp delay={0.1}>
                    <div className="mt-24 md:mt-32 border-t hairline pt-10 md:pt-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <p className="font-display italic text-4xl md:text-7xl tracking-tight leading-[1]">
                            <span className="text-gold">Feel</span> before you build.
                        </p>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-[0.32em] text-muted-foreground">
                                Signed
                            </div>
                            <div className="font-display text-xl mt-1">Chandak Ankit</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                                Founder &amp; CEO
                            </div>
                        </div>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}
